import {
    Box,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    MenuItem,
    FormHelperText,
    TextField,
    Accordion,
    AccordionSummary,
    Typography,
    AccordionDetails,
    InputAdornment,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOffIcon from "@mui/icons-material/EditOff";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ArrayPath, Controller, FieldValues, Path, PathValue, useFieldArray, useFormContext } from "react-hook-form";
import { MAIN_RULES } from "../../../constants/formRules";
import { RESIDENT_RULES } from "../../../constants/formRules/resident";
import BuildingSelect from "../../Autocomplete/BuildingSelect";
import CitiesSelect from "../../Autocomplete/CitiesSelect";
import StreetSelect from "../../Autocomplete/StreetSelect";
import { useState, useContext } from "react";
import { MainContextState, MainContext } from "../../../context/MainContext";
import { EntityType } from "../../../constants/resident";
import { Contract } from "../../../types/api/contracts";
import { BASE_IMG_URL } from "../../../constants/mainAPI";
import { EquipmentData } from "../../../types/api/equipment";
import { DocumentData } from "../../../types/api/documents";
import { getDateTimeFormat } from "../../../utils/getDateFormat";
import { MoneyField } from "../../UI/MoneyField/MoneyField";
import { InnSelect } from "../../Autocomplete/InnSelect";
import { useParams, useSearchParams } from "react-router-dom";
import { ResidentNameSelect } from "../../Autocomplete/ResidentNameSelect";

const addressInitialState = {
    cityId: "",
    streetId: "",
    zip: "",
};

interface IProps {
    editForm?: boolean;
    equipments?: Array<EquipmentData>;
    viewDocuments?: Array<DocumentData>;
    setViewDocuments?: (value: Array<DocumentData> | undefined) => void;
}

export const ResidentForm = <T extends FieldValues>({
    editForm = false,
    equipments,
    viewDocuments,
    setViewDocuments,
}: IProps) => {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { control, setValue, watch } = useFormContext<T>();
    const [address, setAddress] = useState(addressInitialState);
    const [selectResidentId, setSelectResidentId] = useState("");

    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };
    const [deleteContracts, setDeleteContracts] = useState<Array<number>>([]);
    const { constants } = useContext<Partial<MainContextState>>(MainContext);

    const isEntityCreate = Number(watch("type" as Path<T>)) === EntityType.LegalEntity;

    const contractsEditArray = useFieldArray({
        control: control,
        name: "contracts" as ArrayPath<T>,
        keyName: "id_key",
    });

    const addContractsEditInput = () => {
        contractsEditArray.append({
            sum: 0,
            type: 0,
            from: "",
            to: "",
            contractable_type: "",
            contractable_id: 0,
            contractable_count: 0,
        } as any);
    };

    const documentsEditArray = useFieldArray({
        control: control,
        name: "documents" as ArrayPath<T>,
    });

    const addDocumentsEditInput = () => {
        documentsEditArray.append({} as any);
    };

    return (
        <>
            <Box
                component="form"
                sx={{
                    "& .MuiTextField-root": { my: 1 },
                }}
                noValidate
                autoComplete="off">
                <Controller
                    defaultValue={" " as PathValue<T, Path<T>>}
                    name={"type" as Path<T>}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value }, fieldState }) => (
                        <FormControl sx={{ width: "100%" }}>
                            <InputLabel id={"resident-type-label"}>Тип резидента</InputLabel>
                            <Select
                                size={"small"}
                                fullWidth
                                labelId={"resident-type-label"}
                                label={"Тип резидента"}
                                onChange={(event: SelectChangeEvent) => {
                                    onChange(event.target.value);
                                }}
                                value={value}
                                defaultValue={" "}
                                autoComplete="off"
                                error={!!fieldState.error}>
                                {constants?.resident_type.map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.text}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{fieldState.error?.message}</FormHelperText>
                        </FormControl>
                    )}
                />
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"name" as Path<T>}
                    control={control}
                    rules={{
                        required: true,
                        maxLength: MAIN_RULES.name,
                    }}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
                        return (
                            <ResidentNameSelect
                                id={String(id)}
                                onChange={val => {
                                    onChange(val ?? "");
                                }}
                                callbackResult={item => {
                                    if (item.id) {
                                        setOpen(true);
                                        setSelectResidentId(String(item.id));
                                    }
                                }}
                                error={error}
                                value={value}
                                label={isEntityCreate ? "Название" : "ФИО"}
                            />
                        );
                    }}
                />
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"phone" as Path<T>}
                    control={control}
                    rules={{ required: true, pattern: MAIN_RULES.phone }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Телефон"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"email" as Path<T>}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Почта"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"birthday" as Path<T>}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            type="date"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label="Дата рождения"
                            onChange={(e: any) => {
                                onChange(e.target.value.replace(/-/g, "/"));
                            }}
                            value={value?.replace(/\//g, "-")}
                            autoComplete="off"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                {editForm && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Статус</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Controller
                                defaultValue={undefined as PathValue<T, Path<T>>}
                                name={"status.state" as Path<T>}
                                control={control}
                                render={({ field: { onChange, value }, fieldState }) => (
                                    <FormControl sx={{ width: "100%", my: 1 }}>
                                        <InputLabel id={"resident-type-label"}>Состояние статуса</InputLabel>
                                        <Select
                                            size={"small"}
                                            fullWidth
                                            labelId={"resident-type-label"}
                                            label={"Состояние статуса"}
                                            onChange={(event: SelectChangeEvent) => {
                                                onChange(event.target.value);
                                            }}
                                            value={String(value)}
                                            defaultValue={" "}
                                            autoComplete="off"
                                            error={!!fieldState.error}>
                                            {constants?.resident_state.map(state => (
                                                <MenuItem key={state.value} value={state.value}>
                                                    {state.text}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                defaultValue={" " as PathValue<T, Path<T>>}
                                name={"status.message" as Path<T>}
                                control={control}
                                rules={{
                                    maxLength: MAIN_RULES.message,
                                }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label={"Комментарий"}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </AccordionDetails>
                    </Accordion>
                )}
                {!isEntityCreate && (
                    <Controller
                        defaultValue={"" as PathValue<T, Path<T>>}
                        name={"requisites.snils" as Path<T>}
                        control={control}
                        rules={{ required: true, pattern: RESIDENT_RULES.snils }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                size={"small"}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                label={"СНИЛС"}
                                type={"number"}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                )}

                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"requisites.inn" as Path<T>}
                    control={control}
                    rules={{ required: true, pattern: RESIDENT_RULES.inn }}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
                        return (
                            <InnSelect
                                id={String(id)}
                                onChange={val => {
                                    onChange(val);
                                }}
                                callbackResult={item => {
                                    if (item.id) {
                                        setOpen(true);
                                        setSelectResidentId(String(item.id));
                                    }
                                }}
                                error={error}
                                value={value}
                            />
                        );
                    }}
                />
                {isEntityCreate && (
                    <>
                        <Controller
                            defaultValue={"" as PathValue<T, Path<T>>}
                            name={"chief_name" as Path<T>}
                            control={control}
                            rules={{ required: true }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    size={"small"}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    fullWidth
                                    label={"Руководитель"}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Адрес</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Controller
                                    defaultValue={"" as PathValue<T, Path<T>>}
                                    name={"address.city" as Path<T>}
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                                        <CitiesSelect
                                            onChange={onChange}
                                            error={error}
                                            value={value}
                                            callbackResult={city => {
                                                setAddress({ ...address, cityId: city?.data?.fias_id });
                                                // setValue("address.coords", building.coords);
                                                // setValue("address.index", building.zip);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    defaultValue={"" as PathValue<T, Path<T>>}
                                    name={"address.street" as Path<T>}
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                                        <StreetSelect
                                            onChange={onChange}
                                            value={value}
                                            error={error}
                                            disabled={!address.cityId}
                                            callbackResult={street => {
                                                setAddress({ ...address, streetId: street?.data?.fias_id });
                                            }}
                                            cityId={address.cityId}
                                        />
                                    )}
                                />
                                <Controller
                                    defaultValue={"" as PathValue<T, Path<T>>}
                                    name={"address.building" as Path<T>}
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                                        <BuildingSelect
                                            onChange={onChange}
                                            value={value}
                                            error={error}
                                            disabled={!address.streetId}
                                            callbackResult={building => {
                                                setAddress({ ...address, zip: building?.data?.postal_code ?? "" });
                                                setValue(
                                                    "address.coords" as Path<T>,
                                                    JSON.stringify([
                                                        building?.data?.geo_lat,
                                                        building?.data?.geo_lon,
                                                    ]) as PathValue<T, keyof T & Path<T>>,
                                                );
                                                setValue(
                                                    "address.index" as Path<T>,
                                                    (building?.data?.postal_code ?? "") as PathValue<
                                                        T,
                                                        keyof T & Path<T>
                                                    >,
                                                );
                                            }}
                                            streetId={address.streetId}
                                        />
                                    )}
                                />

                                <Controller
                                    defaultValue={"" as PathValue<T, Path<T>>}
                                    name={"address.index" as Path<T>}
                                    control={control}
                                    rules={{ required: true, pattern: MAIN_RULES.address_index }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            size={"small"}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            label={"Индекс"}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Реквизиты</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Controller
                                    defaultValue={null as PathValue<T, Path<T>>}
                                    name={"requisites.bik" as Path<T>}
                                    control={control}
                                    rules={{ required: true, pattern: RESIDENT_RULES.bik }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            size={"small"}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            label={"БИК"}
                                            type={"number"}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    defaultValue={null as PathValue<T, Path<T>>}
                                    name={"requisites.kpp" as Path<T>}
                                    control={control}
                                    rules={{ required: true, pattern: RESIDENT_RULES.kpp }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            size={"small"}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            label={"КПП"}
                                            type={"number"}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    defaultValue={null as PathValue<T, Path<T>>}
                                    name={"requisites.account" as Path<T>}
                                    control={control}
                                    rules={{ required: true, pattern: RESIDENT_RULES.account }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            size={"small"}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            label={"Р/С"}
                                            type={"number"}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </>
                )}
                {!isEntityCreate && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Паспорт</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Controller
                                defaultValue={"" as PathValue<T, Path<T>>}
                                name={`requisites.passport_seria` as Path<T>}
                                control={control}
                                rules={{ required: true, pattern: MAIN_RULES.passport_seria }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label={"Серия"}
                                        type={"number"}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                defaultValue={"" as PathValue<T, Path<T>>}
                                name={`requisites.passport_number` as Path<T>}
                                control={control}
                                rules={{ required: true, pattern: MAIN_RULES.passport_number }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label={"Номер"}
                                        type={"number"}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                defaultValue={"" as PathValue<T, Path<T>>}
                                name={"requisites.passport_date" as Path<T>}
                                control={control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value, ...field }, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        type="date"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label="Дата выдачи"
                                        onChange={(e: any) => {
                                            onChange(e.target.value.replace(/-/g, "/"));
                                        }}
                                        value={value?.replace(/\//g, "-")}
                                        autoComplete="off"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                defaultValue={"" as PathValue<T, Path<T>>}
                                name={"requisites.passport_place" as Path<T>}
                                control={control}
                                rules={{ required: true }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label={"Кем выдан"}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                defaultValue={"" as PathValue<T, Path<T>>}
                                name={"requisites.passport_place_code" as Path<T>}
                                control={control}
                                rules={{ required: true, pattern: MAIN_RULES.passport_place_code }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label={"Код подразделения"}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </AccordionDetails>
                    </Accordion>
                )}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Контракты</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Table aria-label="contracts table" sx={{ "& td": { border: "none" } }}>
                            <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                                <TableRow>
                                    <TableCell align="center">Сумма</TableCell>
                                    <TableCell align="center">Начало</TableCell>
                                    <TableCell align="center">Окончание</TableCell>
                                    <TableCell align="center">Тип</TableCell>
                                    <TableCell align="center">Кол-во</TableCell>
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contractsEditArray?.fields.map((contract, index) => (
                                    <Row
                                        key={contract.id_key}
                                        index={index}
                                        contract={contract as any}
                                        editForm={editForm}
                                        constants={constants}
                                        equipments={equipments}
                                        deleteContract={() => {
                                            contractsEditArray.remove(index);
                                            // @ts-expect-error later fix
                                            if (editForm && contract.id) {
                                                // @ts-expect-error later fix
                                                setDeleteContracts(prev => [...prev, contract.id]);
                                                setValue(
                                                    "delete_contracts" as Path<T>,
                                                    // @ts-expect-error later fix
                                                    [...deleteContracts, contract.id] as PathValue<
                                                        T,
                                                        keyof T & Path<T>
                                                    >,
                                                );
                                            }
                                        }}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                        <Tooltip title="Добавить контракт">
                            <IconButton aria-label="expand row" size="small" onClick={addContractsEditInput}>
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Документы</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Table aria-label="documents table" sx={{ "& td": { border: "none" } }}>
                            <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                                <TableRow>
                                    <TableCell align="center">Имя</TableCell>
                                    <TableCell align="center">Дата</TableCell>
                                    <TableCell align="center">Скачать</TableCell>
                                    <TableCell align="center">Удалить</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {editForm &&
                                    viewDocuments?.map(({ id, name, created_at, path }, index) => {
                                        return (
                                            <TableRow key={id} sx={{ border: 0 }}>
                                                <TableCell sx={{ wordWrap: "break-word" }}>{name}</TableCell>
                                                <TableCell align="center">{getDateTimeFormat(created_at)}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        aria-label="download-icon"
                                                        size="small"
                                                        href={`${BASE_IMG_URL}/${path}`}>
                                                        <FileDownloadIcon />
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        aria-label="delete-icon"
                                                        size="small"
                                                        onClick={() => {
                                                            setValue(
                                                                `delete_documents.${index}` as Path<T>,
                                                                id as PathValue<T, keyof T & Path<T>>,
                                                            );
                                                            if (setViewDocuments) {
                                                                setViewDocuments(
                                                                    viewDocuments?.filter(doc => doc.id !== id),
                                                                );
                                                            }
                                                        }}>
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                {documentsEditArray.fields.map((document, index) => {
                                    return (
                                        <Controller
                                            defaultValue={{} as PathValue<T, Path<T>>}
                                            name={`documents.${index}` as Path<T>}
                                            control={control}
                                            render={({ field: { onChange, value, ...field }, fieldState }) => (
                                                <TableRow key={document.id} sx={{ border: 0 }}>
                                                    <TableCell align="center" colSpan={3}>
                                                        <TextField
                                                            {...field}
                                                            size={"small"}
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                            fullWidth
                                                            label={"Документ"}
                                                            type={"file"}
                                                            onChange={(e: any) => {
                                                                onChange(e.target.files[0]);
                                                            }}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            aria-label="delete-icon"
                                                            size="small"
                                                            onClick={() => {
                                                                documentsEditArray.remove(index);
                                                            }}>
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        />
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <Tooltip title="Добавить контракт">
                            <IconButton aria-label="expand row" size="small" onClick={addDocumentsEditInput}>
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    </AccordionDetails>
                </Accordion>
            </Box>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Совпадение</DialogTitle>
                <DialogContent>
                    <DialogContentText>Перейти к карточке резидента?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Отмена</Button>
                    <Button
                        onClick={() => {
                            setSearchParams({ residentId: selectResidentId });
                            handleClose();
                        }}>
                        Перейти
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

function Row(props: {
    index: number;
    contract: Contract;
    editForm: any;
    constants: any;
    equipments: any;
    deleteContract: () => void;
}) {
    const [open, setOpen] = useState(false);
    const { index, contract, editForm, constants, equipments, deleteContract } = props;
    return (
        <>
            <TableRow key={contract.id} sx={{ border: 0 }}>
                <TableCell align="center">{contract.sum} &#8381;</TableCell>
                <TableCell align="center">{contract.from.split(" ")[0]}</TableCell>
                <TableCell align="center">{contract.to.split(" ")[0]}</TableCell>
                <TableCell align="center">{contract.type}</TableCell>
                <TableCell align="center">{contract.contractable_count}</TableCell>
                <TableCell align="center">
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => {
                            setOpen(!open);
                        }}>
                        {open ? <EditOffIcon /> : <EditIcon />}
                    </IconButton>
                </TableCell>
                <TableCell align="center">
                    <IconButton aria-label="delete-icon" size="small" onClick={deleteContract}>
                        <DeleteOutlineIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography textAlign={"center"}>Редактирование</Typography>
                            <MoneyField name={`contracts.${index}.sum`} control={editForm.control} label={"Сумма"} />
                            <Controller
                                defaultValue={""}
                                name={`contracts.${index}.from`}
                                control={editForm.control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value, ...field }, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        type="date"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label="Начало"
                                        onChange={(e: any) => {
                                            onChange(e.target.value.replace(/-/g, "/"));
                                        }}
                                        value={value?.replace(/\//g, "-")}
                                        autoComplete="off"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                defaultValue={""}
                                name={`contracts.${index}.to`}
                                control={editForm.control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value, ...field }, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        type="date"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        label="Окончание"
                                        onChange={(e: any) => {
                                            onChange(e.target.value.replace(/-/g, "/"));
                                        }}
                                        value={value?.replace(/\//g, "-")}
                                        autoComplete="off"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                defaultValue={""}
                                name={`contracts.${index}.type`}
                                control={editForm.control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value }, fieldState }) => (
                                    <FormControl sx={{ width: "100%", my: 1 }}>
                                        <InputLabel id={"contract-type-label"}>Тип контракта</InputLabel>
                                        <Select
                                            size={"small"}
                                            fullWidth
                                            labelId={"contract-type-label"}
                                            label={"Тип контракта"}
                                            onChange={(event: SelectChangeEvent) => {
                                                onChange(event.target.value as string);
                                            }}
                                            value={String(value)}
                                            defaultValue={" "}
                                            autoComplete="off"
                                            error={!!fieldState.error}>
                                            {constants?.contract_type.map((type: any) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.text}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                defaultValue={""}
                                name={`contracts.${index}.contractable_id`}
                                control={editForm.control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value }, fieldState }) => (
                                    <FormControl sx={{ width: "100%", my: 1 }}>
                                        <InputLabel id={"contractable-id-label"}>Оборудование</InputLabel>
                                        <Select
                                            size={"small"}
                                            fullWidth
                                            labelId={"contractable-id-label"}
                                            label={"Оборудование"}
                                            onChange={(event: SelectChangeEvent) => {
                                                onChange(event.target.value as string);
                                            }}
                                            value={String(value)}
                                            defaultValue={" "}
                                            autoComplete="off"
                                            error={!!fieldState.error}>
                                            {equipments?.map((equipment: any) => (
                                                <MenuItem key={equipment.id} value={equipment.id}>
                                                    {equipment.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                defaultValue={""}
                                name={`contracts.${index}.contractable_count`}
                                control={editForm.control}
                                rules={{ required: true }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        size={"small"}
                                        fullWidth
                                        label={"Кол-во оборуд."}
                                        type={"number"}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">ед.</InputAdornment>,
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}
